<template>
  <div
    class="flex h-16 min-w-[200px] cursor-pointer flex-row items-center rounded-xl align-middle shadow hover:bg-light-2 hover:dark:bg-dark-3 md:min-w-[300px] md:flex-row"
    :class="[
      isActiveBuilding
        ? 'border-b-2 border-primary-2 bg-light-2 dark:bg-dark-3'
        : 'border-dark-3 bg-light-1 dark:border dark:bg-dark-2',
    ]"
    :data-testid="isActiveBuilding ? 'buildingCard_active' : 'buildingCard'"
  >
    <div
      class="flex h-full grow flex-row items-center pl-4 font-bold"
      @click="changeActiveBuilding(building)"
    >
      <em
        v-if="!building?.isAccessible"
        class="mdi mr-2 text-2xl"
        :class="impedimentIconClass(building.typeNotAccessible)"
      />
      <div class="flex flex-row items-center w-full">
        <div class="flex flex-col flex-grow">
          <span>
            {{ building.getLabel() }}
          </span>
          <span class="truncate text-sm font-thin">
            {{ building.getEntranceInfo() }}
          </span>
        </div>
        <div v-if="containsUnfinishedRoomGroups">
          <em
            class="mdi mdi-help-circle ml-4 text-2xl text-light-error dark:text-dark-error"
          />
        </div>
        <div v-if="isUnverifiedBuilding">
          <em
            class="mdi mdi-alert-circle-outline ml-4 text-2xl text-light-warning dark:text-dark-warning"
          />
        </div>
        <div
          v-if="
            !isCompatible &&
            (isOnSiteInspectionOrder || orderStore.isSmartHeatingOrder())
          "
        >
          <em
            class="mdi mdi-close-octagon-outline ml-4 text-2xl text-light-warning dark:text-dark-warning"
          />
        </div>
      </div>
    </div>

    <OptionsDropdown
      class="pr-2"
      :data-testid="
        isActiveBuilding
          ? 'buildingOptionsDropdown_active'
          : 'buildingOptionsDropdown'
      "
      icon="mdi-dots-vertical"
    >
      <li
        v-if="!building.isAccessible"
        class="text-light-warning dark:text-dark-warning"
        @click="editBuilding(building, { scrollToBottom: true })"
      >
        <em class="mdi mdi-lock mr-2" />Hinderungsgrund aktualisieren
      </li>
      <li data-testid="editBuildingButton" @click="editBuilding(building)">
        <em class="mdi mdi-pencil mr-2" />Bearbeiten
      </li>
      <li
        v-if="isOnSiteInspectionOrder"
        data-testid="isBuildingCompatibleButton"
        @click="handleIsBuildingCompatible(building)"
      >
        <em class="mdi mdi-close-circle-outline mr-2" />Gebäude<span
          v-if="inspectedBuilding.compatibility?.isCompatible"
        >
          nicht</span
        >
        ausstattungsfähig
      </li>
      <li
        data-testid="deleteBuildingButton"
        :class="[
          {
            'cursor-not-allowed text-light-disabled-text hover:bg-transparent dark:text-dark-disabled-text':
              !isBuildingDeletable,
          },
        ]"
        @click="isBuildingDeletable && togglePrompt()"
      >
        <em class="mdi mdi-delete mr-2" />Löschen
      </li>
    </OptionsDropdown>
  </div>
  <BasePrompt
    :open="promptOpen"
    :cancel="togglePrompt"
    :close="togglePrompt"
    :proceed="onDeleteBuilding"
    title="Gebäude löschen"
    question="Sind Sie sicher, dass Sie dieses Gebäude löschen möchten?"
  />
</template>
<script setup lang="ts">
import { Building } from '@/models';
import BasePrompt from '@/components/UI/Modal/BasePrompt.vue';
import { ref, computed, inject } from 'vue';
import OptionsDropdown from '@/components/UI/Dropdown/OptionsDropdown.vue';
import { useBuildingCard } from '@/composables/useBuildingCard';
import { useOrderStore } from '@/store/order/orderStore';
import { useInspectedBuildingStore } from '@/store/entities/inspectedBuildingStore';
import { entityTypes, impedimentIconClass } from '@/enums/generic';

const orderStore = useOrderStore();

const props = defineProps<{
  building: Building;
  isActiveBuilding: boolean;
}>();

const changeActiveBuilding = inject('changeActiveBuilding');
const editBuilding = inject('editBuilding');
const handleIsBuildingCompatible = inject('handleIsBuildingCompatible');

const promptOpen = ref(false);
const togglePrompt = () => {
  promptOpen.value = !promptOpen.value;
};

const building = computed(() => {
  return props.building;
});

const inspectedBuilding = computed(() => {
  return useInspectedBuildingStore().getInspectedBuildingByBuildingId(
    building.value.id
  );
});

const containsUnfinishedRoomGroups = computed(() => {
  return Array.from(
    orderStore.unfinishedSmartHeatingRoomGroups().values()
  ).some((rg) => rg.buildingId === building.value.id);
});

const isUnverifiedBuilding = computed(() => {
  return (
    orderStore
      .getOrderChanges()
      ?.hasUnverifiedEntity(entityTypes.building, building.value.id) ?? false
  );
});

const isCompatible = computed(() => {
  if (!inspectedBuilding.value) return true;
  return inspectedBuilding.value.compatibility?.isCompatible;
});

const isOnSiteInspectionOrder = computed(() => {
  return orderStore.isOnSiteInspection();
});

const { isBuildingDeletable, onDeleteBuilding } = useBuildingCard(building);
</script>
